import { System } from '@ecs/System';
import { Position } from '@components/Position';
import { Velocity } from '@components/Velocity';
import { ViewportEntry } from '@components/ViewportEntry';
import { Tag } from '@components/Tag';

/**
 * Keeps enemies in play. Enemies that drift off-screen (e.g. hunters with no target
 * in range, or snipers backing away) would otherwise wander forever and count
 * against the level's max enemy cap, stalling spawns.
 */
export class EnemyBoundarySystem extends System {
  private width = 800;
  private height = 600;
  private readonly exitMargin = 40; // allowed overshoot once an enemy has been on-screen
  private readonly spawnMargin = 120; // allowed distance outside before first entry

  setBounds(width: number, height: number): void {
    this.width = width;
    this.height = height;
  }

  update(_deltaTime: number): void {
    const enemies = this.getEntitiesWithComponents(Position, Velocity, ViewportEntry, Tag);

    for (const enemy of enemies) {
      if (enemy.getComponent(Tag)!.value !== 'enemy') continue;

      const position = enemy.getComponent(Position)!;
      const velocity = enemy.getComponent(Velocity)!;
      const entry = enemy.getComponent(ViewportEntry)!;

      const overshoot = this.distanceOutside(position);
      if (overshoot === 0) {
        entry.hasEntered = true;
        continue;
      }

      const margin = entry.hasEntered ? this.exitMargin : this.spawnMargin;
      if (overshoot <= margin) continue;

      velocity.angle = Math.atan2(this.height / 2 - position.y, this.width / 2 - position.x);
      const returnSpeed = Number.isFinite(velocity.maxSpeed) ? velocity.maxSpeed * 0.75 : 1;
      velocity.speed = Math.max(velocity.speed, returnSpeed);
    }
  }

  private distanceOutside(position: Position): number {
    const dx = Math.max(0 - position.x, position.x - this.width, 0);
    const dy = Math.max(0 - position.y, position.y - this.height, 0);
    return Math.max(dx, dy);
  }
}
